import { z } from 'zod';
import { ALLOCATION_KEYS, COST_CATEGORIES, HEATING_TYPES, METERING_PROVIDERS, PROPERTY_TYPES, UNIT_KINDS, UNIT_STATUSES } from '../enums';
import { zAddress, zDate, zId, zMonthDay } from './common';
import { AREA_ROOM_TYPES, areaRequired, woflvArea } from '../areas';
import { ANNEX_KINDS, COMMON_ROOM_KINDS, OUTDOOR_KINDS } from '../estate';

/** Room list of the Flächenrechner, stored as evidence next to an area slice (docs/15 #17); the engine reads only `area`. */
export const zAreaRoom = z.object({ name: z.string().trim().max(60).default(''), centiM2: z.number().int().min(0).max(10_000_000), type: z.enum(AREA_ROOM_TYPES) });
export const zAreaRooms = z.array(zAreaRoom).min(1).max(100);
/** The stored rooms must give exactly the area they explain (Σ WoFlV, rounded once to 0,01 m²). */
export const roomsMatchArea = (rooms: { centiM2: number; type: (typeof AREA_ROOM_TYPES)[number] }[], area: number) => woflvArea(rooms).totalCentiM2 === Math.round(area * 100);

const zEstateId = z.string().trim().min(1).max(40).optional();
/** Kellerabteil, Dachbodenabteil, Abstellraum, Stellplatz ohne eigene Miete … (ADR-0010): `unitId` = the flat it belongs to. */
export const zAnnex = z.object({ id: zEstateId, kind: z.enum(ANNEX_KINDS), label: z.string().trim().min(1).max(60), unitId: zId.nullable().default(null), usableAreaM2: z.number().positive().max(100000).optional() });
/** Kellerflur, Heizungsraum, Waschkeller … (no unit). `washingMachine` + `userUnitIds`: Nutzergruppe for § 2 Nr. 16 BetrKV. */
export const zCommonRoom = z.object({ id: zEstateId, kind: z.enum(COMMON_ROOM_KINDS), label: z.string().trim().min(1).max(60), washingMachine: z.boolean().optional(), userUnitIds: z.array(zId).max(500).optional() });
export const zOutdoorUsage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('common') }),
  z.object({ type: z.literal('exclusive'), unitIds: z.array(zId).min(1).max(500), maintainedByTenant: z.boolean().default(false) }),
  z.object({ type: z.literal('mixed'), commonPct: z.number().int().min(1).max(99), unitIds: z.array(zId).min(1).max(500) }),
]);
export const zOutdoorArea = z.object({ id: zEstateId, label: z.string().trim().min(1).max(60), kind: z.enum(OUTDOOR_KINDS), areaM2: z.number().positive().max(10_000_000).optional(), usage: zOutdoorUsage });
/** Weight of a child / baby in Personen × Tage (docs/15 #3): quarters 0–1, only when the lease says so. */
export const PERSON_WEIGHT_STEPS = [0, 0.25, 0.5, 0.75, 1] as const;
const zWeight = z.number().refine((w) => (PERSON_WEIGHT_STEPS as readonly number[]).includes(w), { message: 'Gewicht 0, 0,25, 0,5, 0,75 oder 1' });
export const zPersonWeights = z.object({
  children: zWeight,
  babies: zWeight,
  /** As worded in the lease ("Kinder unter 14 Jahren"): shown next to the weight, the engine only knows the groups. */
  childUnderAge: z.number().int().min(1).max(25).optional(),
  babyUnderAge: z.number().int().min(1).max(10).optional(),
});
/** Own "sonstige Betriebskosten" types of a property (BetrKV § 2 Nr. 17, docs/15 #36), e.g. "Dachrinnenreinigung". */
export const zCustomCostType = z.object({ id: z.string().trim().min(1).max(40).optional(), name: z.string().trim().min(2).max(60) });
const estateFields = {
  annexes: z.array(zAnnex).max(500).optional(),
  commonRooms: z.array(zCommonRoom).max(100).optional(),
  outdoorAreas: z.array(zOutdoorArea).max(100).optional(),
};

export const CreatePropertyBody = z.object({
  name: z.string().trim().min(1).max(120),
  address: zAddress.default({}),
  type: z.enum(PROPERTY_TYPES).default('mfh'),
  buildYear: z.number().int().min(1700).max(2100).optional(),
  heating: z.object({ type: z.enum(HEATING_TYPES).default('central_gas'), co2Applicable: z.boolean().default(true), meteringProvider: z.enum(METERING_PROVIDERS).nullable().default(null), consumptionSharePct: z.number().min(50).max(70).optional() }).default({ type: 'central_gas', co2Applicable: true, meteringProvider: null }),
  billingPeriodStart: zMonthDay.default('01-01'),
  totalArea: z.number().positive().optional(),
  totalAreaFrom: zDate.optional(),
  floors: z.array(z.object({ id: z.string().optional(), label: z.string().max(40), order: z.number().int() })).default([]),
  expectedCostCategories: z.array(z.enum(COST_CATEGORIES)).default([]),
  residential: z.boolean().default(true),
  /** WEG: sum of all co-ownership shares (MEA, e.g. 1000 or 10000); the Hausgeld import suggests it (ADR-0016). */
  coOwnershipTotal: z.number().positive().optional(),
  /** Common rooms and outdoor areas without units (annexes need unit ids: set them with PATCH after the units exist). */
  commonRooms: estateFields.commonRooms,
  outdoorAreas: z.array(zOutdoorArea.refine((a) => a.usage.type === 'common', { message: 'Sondernutzung erst nach dem Anlegen der Wohnungen', path: ['usage'] })).max(100).optional(),
});
export const UpdatePropertyBody = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  address: zAddress.optional(),
  type: z.enum(PROPERTY_TYPES).optional(),
  buildYear: z.number().int().min(1700).max(2100).nullable().optional(),
  heating: z.object({ type: z.enum(HEATING_TYPES).optional(), co2Applicable: z.boolean().optional(), meteringProvider: z.enum(METERING_PROVIDERS).nullable().optional(), consumptionSharePct: z.number().min(50).max(70).nullable().optional() }).optional(),
  billingPeriodStart: zMonthDay.optional(),
  floors: z.array(z.object({ id: z.string().optional(), label: z.string().max(40), order: z.number().int() })).optional(),
  /** `isBuilding`: the group is a building ("Haus A"); only those get building cards in the house view (docs/15 #40). */
  groups: z.array(z.object({ id: z.string().optional(), label: z.string().max(40), unitIds: z.array(z.string()), isBuilding: z.boolean().optional() })).optional(),
  expectedCostCategories: z.array(z.enum(COST_CATEGORIES)).optional(),
  /** "Haben wir nicht": cost types this property does not have; never expected, even when last year had them. */
  absentCostCategories: z.array(z.enum(COST_CATEGORIES)).optional(),
  allocationKeys: z.partialRecord(z.enum(COST_CATEGORIES), z.enum(ALLOCATION_KEYS)).optional(),
  residential: z.boolean().optional(),
  coOwnershipTotal: z.number().positive().nullable().optional(),
  archived: z.boolean().optional(),
  /** Personen-Gewichtung laut Mietvertrag (docs/15 #3); null = every person counts 1 (default). */
  personWeights: zPersonWeights.nullable().optional(),
  /** Replaced as a whole list; a type used by a cost item stays (server checks). Names unique per property. */
  customCostTypes: z.array(zCustomCostType).max(30).refine((l) => new Set(l.map((c) => c.name.toLowerCase())).size === l.length, { message: 'Name doppelt' }).optional(),
  /** Replaced as a whole list each (like floors); unit ids must belong to this property (server checks). */
  ...estateFields,
});
export const CreateUnitBody = z.object({
  label: z.string().trim().min(1).max(60),
  kind: z.enum(UNIT_KINDS).default('apartment'),
  floorId: z.string().optional(),
  position: z.string().max(40).optional(),
  /** Treppenhaus / Aufgang: column group in the house plan (docs/15 #40). */
  stairwell: z.string().trim().max(40).optional(),
  billable: z.boolean().optional(),
  /** Wohnfläche (flat) / Nutzfläche (commercial): required. Cellar, storage, parking: optional Nutzfläche, never in the area key (ADR-0010). */
  area: z.number().positive().max(100000).optional(),
  heatingArea: z.number().positive().max(100000).optional(),
  status: z.enum(UNIT_STATUSES).default('rentable'),
  existsFrom: zDate,
  coOwnershipShare: z.number().positive().optional(),
  features: z.array(z.string().max(40)).max(30).default([]),
  /** Flächenrechner rooms behind `area` (evidence only); must add up to `area`. */
  areaRooms: zAreaRooms.optional(),
}).refine((v) => v.area !== undefined || !areaRequired(v.kind), { message: 'Fläche fehlt', path: ['area'] })
  .refine((v) => !v.areaRooms || (v.area !== undefined && roomsMatchArea(v.areaRooms, v.area)), { message: 'Raumliste ergibt eine andere Fläche', path: ['areaRooms'] });
export const UpdateUnitBody = z.object({
  label: z.string().trim().min(1).max(60).optional(),
  floorId: z.string().nullable().optional(),
  position: z.string().max(40).nullable().optional(),
  stairwell: z.string().trim().max(40).nullable().optional(),
  billable: z.boolean().optional(),
  coOwnershipShare: z.number().positive().nullable().optional(),
  features: z.array(z.string().max(40)).max(30).optional(),
  /** Position of the unit inside its floor (house drawing, left to right); missing = after the ordered ones. */
  sortIndex: z.number().int().min(0).max(10000).optional(),
});
/** POST /units/:id/backdate: "Besteht seit" moved back (event `unit.backdated`); GET with `?from=` previews it. */
export const BackdateUnitBody = z.object({
  from: zDate,
  note: z.string().trim().max(200).optional(),
});
/** POST /properties/:id/units/order: new left-to-right order of the units of one floor (`null` = without floor). */
export const UnitOrderBody = z.object({
  floorId: z.string().nullable(),
  unitIds: z.array(z.string().min(1)).min(1).max(500).refine((ids) => new Set(ids).size === ids.length, 'duplicate unit ids'),
});
